import React, { useEffect, useState, useRef } from 'react';
import { XIcon } from 'lucide-react';
import { useCart } from './CartContext';
type Collection = 'origen' | 'memoria' | 'pausa';
type QuizStage = 'phase-one' | 'phase-two' | 'result';
type Aroma = 'tierra' | 'aire' | 'agua' | 'hogar' | 'recuerdo' | 'latir' | 'ritual' | 'descanso' | 'silencio';
type PhaseOneQuestion = {
  question: string;
  answers: Array<{
    label: string;
    collection: Collection;
  }>;
};
type AromaResult = {
  collection: Collection;
  title: string;
  notes: string;
  description: string;
  variantId: number;
  image: string;
};
type QuizOverlayProps = {
  isOpen: boolean;
  onClose: () => void;
};
const INITIAL_SCORES: Record<Collection, number> = {
  origen: 0,
  memoria: 0,
  pausa: 0
};
const PHASE_ONE_QUESTIONS: PhaseOneQuestion[] = [{
  question: '¿Qué quieres sentir al llegar a tu espacio?',
  answers: [{
    label: 'Volver a lo esencial y respirar profundo.',
    collection: 'origen'
  }, {
    label: 'La calidez de algo que conozco bien.',
    collection: 'memoria'
  }, {
    label: 'Que el día por fin baje el volumen.',
    collection: 'pausa'
  }]
}, {
  question: '¿Qué momento necesitas cuidar más?',
  answers: [{
    label: 'El comienzo de algo nuevo.',
    collection: 'origen'
  }, {
    label: 'Un recuerdo que todavía me acompaña.',
    collection: 'memoria'
  }, {
    label: 'El momento de detenerme sin culpa.',
    collection: 'pausa'
  }]
}, {
  question: '¿Qué tipo de presencia buscas?',
  answers: [{
    label: 'Clara, natural y con raíces.',
    collection: 'origen'
  }, {
    label: 'Cercana, tierna y familiar.',
    collection: 'memoria'
  }, {
    label: 'Suave, lenta y silenciosa.',
    collection: 'pausa'
  }]
}, {
  question: 'Hoy, tu casa necesita…',
  answers: [{
    label: 'Aire fresco para volver al centro.',
    collection: 'origen'
  }, {
    label: 'Un aroma que abrace la historia.',
    collection: 'memoria'
  }, {
    label: 'Un permiso para descansar.',
    collection: 'pausa'
  }]
}];
const PHASE_TWO_QUESTIONS: Record<Collection, {
  question: string;
  answers: Array<{
    label: string;
    aroma: Aroma;
  }>;
}> = {
  origen: {
    question: '¿Qué tipo de naturaleza te conecta más?',
    answers: [{
      label: 'La tierra después de la lluvia. Madera, profundidad, lo antiguo.',
      aroma: 'tierra'
    }, {
      label: 'El viento fresco de la mañana. Cítricos, ligereza, claridad.',
      aroma: 'aire'
    }, {
      label: 'El mar y lo abierto. Frescura, amplitud, horizonte.',
      aroma: 'agua'
    }]
  },
  memoria: {
    question: '¿Qué recuerdo te da más calidez?',
    answers: [{
      label: 'El olor de casa. Algo cocinando, dulce y muy familiar.',
      aroma: 'hogar'
    }, {
      label: 'Un momento específico. Algo suave que ya no está pero sigue presente.',
      aroma: 'recuerdo'
    }, {
      label: 'El latido de algo vivo. Flores, presencia cercana, alguien querido.',
      aroma: 'latir'
    }]
  },
  pausa: {
    question: '¿Qué tipo de descanso necesitas hoy?',
    answers: [{
      label: 'Un ritual propio. Algo que marque el inicio y el fin del día.',
      aroma: 'ritual'
    }, {
      label: 'Apagar el mundo. Cama temprano, cuerpo tranquilo, nada que hacer.',
      aroma: 'descanso'
    }, {
      label: 'Quietud total. Sin música, sin ruido, solo el aroma y el silencio.',
      aroma: 'silencio'
    }]
  }
};
const RESULTS: Record<Aroma, AromaResult> = {
  tierra: {
    collection: 'origen',
    title: 'Tierra',
    notes: 'Sándalo · Palo Santo',
    description: 'Para quienes necesitan raíces. Profunda, terrosa y antigua como el bosque en la mañana.',
    variantId: 44232110506087,
    image: 'https://cdn.magicpatterns.com/uploads/n6YfL5ePXUcBPg8JmdTyEU/TIERRA_-_VASO.png'
  },
  aire: {
    collection: 'origen',
    title: 'Aire',
    notes: 'Bergamota · Té verde',
    description: 'Para los que necesitan ligereza. Fresca, viva y clara como el primer aire del día.',
    variantId: 44232116043879,
    image: 'https://cdn.magicpatterns.com/uploads/aU4MJVADUod3CcaTD3b1VN/AIRE_-_VASO.png'
  },
  agua: {
    collection: 'origen',
    title: 'Agua',
    notes: 'Acorde marino · Eucalipto',
    description: 'Para los que buscan apertura. Fresca, libre y serena como mirar el horizonte.',
    variantId: 44232121679975,
    image: 'https://cdn.magicpatterns.com/uploads/ukzBe1eg3LFADuWacGyHrQ/AGUA_-_VASO.png'
  },
  hogar: {
    collection: 'memoria',
    title: 'Hogar',
    notes: 'Vainilla · Almendra',
    description: 'Para quienes celebran lo cotidiano. Dulce, cálida y familiar como estar en casa.',
    variantId: 44232124629095,
    image: 'https://cdn.magicpatterns.com/uploads/w9W4eo3ZEBjUw7u3jA3T3N/HOGAR_-_VASO.png'
  },
  recuerdo: {
    collection: 'memoria',
    title: 'Recuerdo',
    notes: 'Manzanilla · Algodón',
    description: 'Para los que honran lo que fue. Suave, tierna y presente como una memoria viva.',
    variantId: 44232126169191,
    image: 'https://cdn.magicpatterns.com/uploads/effjBygjMwyaUSzwSBfZhQ/RECUERDO_-_VASO.png'
  },
  latir: {
    collection: 'memoria',
    title: 'Latir',
    notes: 'Tulipán · Vainilla suave',
    description: 'Para los que sienten cerca a alguien. Viva, floral y cálida como un abrazo.',
    variantId: 44232127545447,
    image: 'https://cdn.magicpatterns.com/uploads/4zeH33145gnNukJRGbZB7B/LATIR_-_VASO.png'
  },
  ritual: {
    collection: 'pausa',
    title: 'Ritual',
    notes: 'Palo Santo · Lavanda',
    description: 'Para los que necesitan marcar el tiempo. Sagrada, limpia y poderosa como un comienzo.',
    variantId: 44232128430183,
    image: 'https://cdn.magicpatterns.com/uploads/vGSeoB2yjvdJ9KDP9rZwgr/RITUAL_-_VASO.png'
  },
  descanso: {
    collection: 'pausa',
    title: 'Descanso',
    notes: 'Manzanilla · Lavanda',
    description: 'Para los que necesitan soltar de verdad. Suave, sedante y gentil como el fin del día.',
    variantId: 44232129118311,
    image: 'https://cdn.magicpatterns.com/uploads/4vDNsXg9cyc47H7i1wxV8D/DESCANSO_-_VASO.png'
  },
  silencio: {
    collection: 'pausa',
    title: 'Silencio',
    notes: 'Algodón · Té negro',
    description: 'Para los que eligen la quietud. Limpia, serena y profunda como la noche.',
    variantId: 44232129773671,
    image: 'https://cdn.magicpatterns.com/uploads/uVc1Jy35kDzMqs86K4xGpM/SILENCIO_-_VASO.png'
  }
};
export function QuizOverlay({
  isOpen,
  onClose
}: QuizOverlayProps) {
  const [stage, setStage] = useState<QuizStage>('phase-one');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [scores, setScores] = useState(INITIAL_SCORES);
  const [collection, setCollection] = useState<Collection>('origen');
  const [aroma, setAroma] = useState<Aroma | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const {
    addItem
  } = useCart();
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled])');
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);
  useEffect(() => {
    if (isOpen) return;
    setStage('phase-one');
    setQuestionIndex(0);
    setScores(INITIAL_SCORES);
    setCollection('origen');
    setAroma(null);
  }, [isOpen]);
  if (!isOpen) return null;
  const currentPhaseOneQuestion = PHASE_ONE_QUESTIONS[questionIndex];
  const phaseTwoQuestion = PHASE_TWO_QUESTIONS[collection];
  const result = aroma ? RESULTS[aroma] : null;
  const progress = stage === 'result' ? 100 : stage === 'phase-two' ? 80 : questionIndex * 20;
  const progressLabel = stage === 'result' ? 'RESULTADO' : `PREGUNTA ${stage === 'phase-two' ? 5 : questionIndex + 1} DE 5`;
  const handlePhaseOneAnswer = (answerCollection: Collection) => {
    const nextScores = {
      ...scores,
      [answerCollection]: scores[answerCollection] + 1
    };
    if (questionIndex < PHASE_ONE_QUESTIONS.length - 1) {
      setScores(nextScores);
      setQuestionIndex(current => current + 1);
      return;
    }
    const winningCollection = getWinningCollection(nextScores);
    setScores(nextScores);
    setCollection(winningCollection);
    setStage('phase-two');
  };
  const handleRestart = () => {
    setStage('phase-one');
    setQuestionIndex(0);
    setScores(INITIAL_SCORES);
    setCollection('origen');
    setAroma(null);
    window.setTimeout(() => closeButtonRef.current?.focus(), 0);
  };
  const handleAddToCart = () => {
    if (!result) return;
    addItem({
      variantId: result.variantId,
      name: result.title,
      aroma: result.notes,
      format: 'Abierta',
      price: 69000,
      image: result.image
    });
    onClose();
  };
  const handleViewCollection = () => {
    if (!result) return;
    onClose();
    try {
      window.sessionStorage.setItem('savia:pendingTab', result.collection);
    } catch {
      // Continue when storage is unavailable.
    }
    window.dispatchEvent(new CustomEvent('savia:tab', {
      detail: result.collection
    }));
    window.setTimeout(() => scrollToCollections(), 0);
  };
  return <div ref={dialogRef} className="quiz-overlay" role="dialog" aria-modal="true" aria-labelledby="quiz-title" aria-describedby="quiz-progress" data-id="element-206" data-mp="2x9qyxid6ghul">
      <header className="quiz-overlay__header" data-id="element-207" data-mp="1lv3kanpk1r7t">
        <p className="quiz-overlay__brand" data-id="element-208" data-mp="1lvwopi11k4ms">SAVIA</p>
        <button ref={closeButtonRef} type="button" className="quiz-overlay__close" onClick={onClose} aria-label="Cerrar buscador de aroma" data-id="element-209" data-mp="1gwa7yym08698">
          <XIcon size={18} strokeWidth={1.5} aria-hidden="true" data-id="element-210" data-mp="1bqwzhwbz97gk" />
        </button>
      </header>

      <main className="quiz-overlay__main" data-id="element-211" data-mp="1gqjdi7zmqrqf">
        <div className="quiz-overlay__progress-wrap" data-id="element-212" data-mp="1gphrj8h2sktx">
          <p id="quiz-progress" className="quiz-overlay__progress-label" aria-live="polite" data-id="element-213" data-mp="1lgpzs16o605i">
            {progressLabel}
          </p>
          <div className="quiz-overlay__progress-track" role="progressbar" aria-label="Progreso del buscador de aroma" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress} data-id="element-214" data-mp="1pkxohyd4ng3q">
            <div className="quiz-overlay__progress-fill" style={{
            width: `${progress}%`
          }} data-id="element-215" data-mp="24fc19h63lt9y" />
          </div>
        </div>

        {stage !== 'result' && <section className="quiz-overlay__question" aria-live="polite" data-id="element-216" data-mp="a3nu4163yv04">
            <p className="quiz-overlay__eyebrow" data-id="element-217" data-mp="21n5rqofmtcl">ENCUENTRA TU AROMA</p>
            <h1 id="quiz-title" data-id="element-218" data-mp="3t8hmewvme6es">
              {stage === 'phase-one' ? currentPhaseOneQuestion.question : phaseTwoQuestion.question}
            </h1>
            <div className="quiz-overlay__answers" role="group" aria-label="Opciones de respuesta" data-id="element-219" data-mp="6zopmdtomwp4">
              {(stage === 'phase-one' ? currentPhaseOneQuestion.answers : phaseTwoQuestion.answers).map((answer, index) => <button key={answer.label} type="button" className="quiz-answer" onClick={() => stage === 'phase-one' ? handlePhaseOneAnswer((answer as {
            collection: Collection;
          }).collection) : (() => {
            setAroma((answer as {
              aroma: Aroma;
            }).aroma);
            setStage('result');
          })()} data-id="element-220" data-mp="uohghvvg6ecv" data-mpi={index}>
                  <span className="quiz-answer__index" aria-hidden="true" data-id="element-221" data-mp="14dy5mjaslrsk">
                    {String.fromCharCode(65 + index)}
                  </span>
                  <span data-id="element-222" data-mp="qjx1tb4zhelc">{answer.label}</span>
                </button>)}
            </div>
          </section>}

        {stage === 'result' && result && <section className="quiz-result" aria-live="polite" data-id="element-223" data-mp="gp53g53kunpi">
            <p className="quiz-result__supertitle" data-id="element-224" data-mp="lngf8ldfn8ad">TU VELA ES</p>
            <h1 id="quiz-title" className="quiz-result__title" data-id="element-225" data-mp="lozymkudxzgd">
              {result.title}
            </h1>
            <p className="quiz-result__notes" data-id="element-226" data-mp="1o25xzf2awyh3">{result.notes}</p>
            <p className="quiz-result__description" data-id="element-227" data-mp="1jz9hbtwbncfs">{result.description}</p>
            <p className="quiz-result__price" data-id="element-228" data-mp="1f6zkdtwshc40">Vela desde $69.000</p>
            <div className="quiz-result__actions" data-id="element-229" data-mp="1a96g56f1qs6d">
              <button type="button" className="quiz-result__primary" onClick={handleAddToCart} data-id="element-230" data-mp="1a8evgqmzmeuv">
                AGREGAR AL CARRITO
              </button>
              <button type="button" className="quiz-result__secondary" onClick={handleViewCollection} data-id="element-231" data-mp="10cv2abzt5u9t">
                VER COLECCIÓN {result.collection.toUpperCase()}
              </button>
            </div>
            <button type="button" className="quiz-result__restart" onClick={handleRestart} data-id="element-232" data-mp="2qlvaqd4st20e">
              Volver a empezar
            </button>
          </section>}
      </main>
    </div>;
}
function getWinningCollection(scores: Record<Collection, number>): Collection {
  return (['origen', 'memoria', 'pausa'] as Collection[]).reduce((winner, candidate) => scores[candidate] > scores[winner] ? candidate : winner);
}
function scrollToCollections() {
  const target = document.getElementById('colecciones');
  if (!target) return;
  const nav = document.getElementById('nav');
  const offset = (nav?.offsetHeight ?? 0) + 24;
  window.scrollTo({
    top: target.getBoundingClientRect().top + window.scrollY - offset,
    behavior: 'smooth'
  });
}